"use client";

import { c as _c } from "react/compiler-runtime";
import { AnimatePresence, motion } from "framer-motion";
import { ContactStep } from "@/lib/types";
interface ContactStepRowProps {
  step: ContactStep;
  index: number;
  active: boolean;
  /** Auto-play is still driving, so this row should show its countdown. */
  running: boolean;
  intervalMs: number;
  onSelect: (index: number) => void;
}

// One row of the stepper. Collapsed it is a title and a marker; selected it
// opens to show the copy and, while auto-play is still running, a bar counting
// down to the next step — so the movement is explained rather than surprising.
const ContactStepRow = t0 => {
  const $ = _c(42);
  const {
    step,
    index,
    active,
    running,
    intervalMs,
    onSelect
  } = t0;
  const Icon = step.icon;
  let t1;
  if ($[0] !== index || $[1] !== onSelect) {
    t1 = () => onSelect(index);
    $[0] = index;
    $[1] = onSelect;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const t2 = active ? 1 : 0.82;
  let t3;
  if ($[3] !== t2) {
    t3 = {
      scale: t2
    };
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      type: "spring",
      stiffness: 320,
      damping: 22
    };
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const t5 = `grid size-10 place-items-center rounded-full border transition-colors duration-300 md:size-12 ${active ? "border-primary bg-primary text-black" : "border-black/10 bg-white text-black/40 group-hover/row:border-primary group-hover/row:text-black/70"}`;
  let t6;
  if ($[6] !== Icon) {
    t6 = <Icon size={18} />;
    $[6] = Icon;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t3 || $[9] !== t5 || $[10] !== t6) {
    t7 = <span className="relative z-10 shrink-0"><motion.span animate={t3} transition={t4} className={t5}>{t6}</motion.span></span>;
    $[8] = t3;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const t8 = `text-[10px] font-black tracking-[0.3em] transition-colors duration-300 ${active ? "text-[#8ba61e]" : "text-black/25"}`;
  let t9;
  if ($[12] !== step.number || $[13] !== t8) {
    t9 = <span className={t8}>{step.number}</span>;
    $[12] = step.number;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const t10 = `text-lg font-black uppercase tracking-tighter transition-colors duration-300 md:truncate md:text-xl ${active ? "text-neutral-900" : "text-black/40 group-hover/row:text-black/70"}`;
  let t11;
  if ($[15] !== step.title || $[16] !== t10) {
    t11 = <span className={t10}>{step.title}</span>;
    $[15] = step.title;
    $[16] = t10;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== t11 || $[19] !== t9) {
    t12 = <span className="flex items-baseline gap-3">{t9}{t11}</span>;
    $[18] = t11;
    $[19] = t9;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== active || $[22] !== step.copy) {
    t13 = active && <motion.span key="copy" initial={{
      opacity: 0,
      height: 0
    }} animate={{
      opacity: 1,
      height: "auto"
    }} exit={{
      opacity: 0,
      height: 0
    }} transition={{
      duration: 0.35,
      ease: [0.22, 1, 0.36, 1]
    }} className="block overflow-hidden"><span className="block pt-2 text-sm leading-relaxed text-neutral-500">{step.copy}</span></motion.span>;
    $[21] = active;
    $[22] = step.copy;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== t13) {
    t14 = <AnimatePresence initial={false}>{t13}</AnimatePresence>;
    $[24] = t13;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== active || $[27] !== index || $[28] !== intervalMs || $[29] !== running) {
    t15 = active && running && <span className="mt-4 block h-px w-full overflow-hidden bg-black/10"><motion.span key={index} initial={{
        scaleX: 0
      }} animate={{
        scaleX: 1
      }} transition={{
        duration: intervalMs / 1000,
        ease: "linear"
      }} className="block h-full w-full origin-left bg-primary" /></span>;
    $[26] = active;
    $[27] = index;
    $[28] = intervalMs;
    $[29] = running;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== t12 || $[32] !== t14 || $[33] !== t15) {
    t16 = <span className="min-w-0 flex-1">{t12}{t14}{t15}</span>;
    $[31] = t12;
    $[32] = t14;
    $[33] = t15;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== t16 || $[36] !== t7) {
    t17 = <div className="flex items-start gap-4 md:gap-5">{t7}{t16}</div>;
    $[35] = t16;
    $[36] = t7;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== active || $[39] !== t1 || $[40] !== t17) {
    t18 = <button type="button" onClick={t1} aria-current={active} className="group/row relative w-full cursor-pointer py-5 text-left">{t17}</button>;
    $[38] = active;
    $[39] = t1;
    $[40] = t17;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  return t18;
};
export default ContactStepRow;